import { styled } from '@linaria/react';
import { Group } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePullRequestFileLines } from '../../../../../api/agent-miner-client/agent-miner-client.hooks';
import type { SessionPullRequest } from '../../../../../api/agent-miner-client/agent-miner-client.type';
import { BrandedLoadingOverlay } from '../../../../../components/loader/branded-loader';
import {
  baseWhite,
  inkLight,
  secondaryDarkest,
  secondaryLightest,
  skyLight,
} from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Select } from '../../../../../ui-library/select/select';
import { fmtNum } from '../../../shared/formatters';
import { Additions, Card, Deletions, Mono } from '../pr-detail.helpers';
import { defaultFileId, fileKeptPct } from '../pr-survival.helpers';
import { AgentEditsView } from './agent-edits-view';
import { FileDiffView } from './file-diff-view';

type PrCodeViewProps = {
  pr: SessionPullRequest;
  sessionId: string;
  selectedFileId: string | null;
  onSelectFile: (fileId: string) => void;
  /** Show an inline file picker (used when the side panel's file tree is collapsed). */
  showFilePicker: boolean;
};

type View = 'diff' | 'edits';

/**
 * The Code view of a pull request: a sticky file header (path, +/-, kept
 * share, merged-diff / agent-edits toggle, and an inline file picker when the
 * side panel is closed) above the selected file's tagged diff. Only the
 * selected file is fetched; the query hook caches per file.
 *
 * @param {PrCodeViewProps} props - The PR, viewed session, selection, and picker flag
 * @returns {JSX.Element} The code view
 */
export function PrCodeView({ pr, sessionId, selectedFileId, onSelectFile, showFilePicker }: PrCodeViewProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('diff');
  const files = pr.files;
  const effectiveFileId =
    selectedFileId && files.some((file) => file.change_request_file_id === selectedFileId)
      ? selectedFileId
      : defaultFileId(files);
  const file = files.find((candidate) => candidate.change_request_file_id === effectiveFileId) ?? null;
  const { data, query } = usePullRequestFileLines(sessionId, pr.pr_id, effectiveFileId);

  if (files.length === 0 || file === null) {
    return <Message>{t('agent_miner.pull_requests.code.no_files')}</Message>;
  }

  // A missing flag defaults to precise so legacy/cached payloads keep the current
  // rendering; only an explicit false is non-precise. Mirrors pr-file-tree.tsx so
  // the sticky header never shows "0% kept" for a file the tree marks "—".
  const keptPct = file.line_precise !== false ? fileKeptPct(file) : null;

  return (
    <Card>
      <StickyHeader>
        <PathBlock>
          <Path title={file.filename ?? ''}>{file.filename}</Path>
          <Additions>+{fmtNum(file.additions)}</Additions>
          <Deletions>−{fmtNum(file.deletions)}</Deletions>
          {keptPct !== null && <KeptChip>{t('agent_miner.pull_requests.code.kept_pct', { pct: keptPct })}</KeptChip>}
        </PathBlock>
        <Group gap={8} wrap="wrap">
          {showFilePicker && (
            <FilePicker>
              <Select
                data-bf="pr-file-picker"
                size="xs"
                value={effectiveFileId}
                options={files.map((candidate) => ({
                  value: candidate.change_request_file_id,
                  label: candidate.filename ?? candidate.change_request_file_id,
                }))}
                onChange={onSelectFile}
                enableTooltip
              />
            </FilePicker>
          )}
          <Button
            size="xs"
            radius="xl"
            variant={view === 'diff' ? 'secondary' : 'outline'}
            aria-pressed={view === 'diff'}
            onClick={() => setView('diff')}
          >
            {t('agent_miner.pull_requests.code.view_diff')}
          </Button>
          <Button
            size="xs"
            radius="xl"
            variant={view === 'edits' ? 'secondary' : 'outline'}
            aria-pressed={view === 'edits'}
            onClick={() => setView('edits')}
          >
            {t('agent_miner.pull_requests.code.view_edits')}
          </Button>
        </Group>
      </StickyHeader>
      <Panel>
        {query.isLoading && (
          <LoadingWrap>
            <BrandedLoadingOverlay visible variant="colored" scoped />
          </LoadingWrap>
        )}
        {query.isError && (
          <Group gap={12} align="center">
            <Message>{t('agent_miner.pull_requests.code.error')}</Message>
            <Button variant="outline" size="xs" radius="xl" onClick={() => query.refetch()}>
              {t('agent_miner.pull_requests.code.retry')}
            </Button>
          </Group>
        )}
        {data &&
          !query.isError &&
          (view === 'diff' ? (
            // Key by the selected file so FileDiffView's expand/showHidden state
            // does not leak into the next file on a cached revisit.
            <FileDiffView key={effectiveFileId} data={data} currentSessionId={sessionId} />
          ) : (
            <AgentEditsView
              edits={data.edits}
              currentSessionId={sessionId}
              patchAvailable={data.patch_available}
              authored={file.authored}
              survived={file.survived}
              linePrecise={file.line_precise !== false}
            />
          ))}
      </Panel>
    </Card>
  );
}

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: -18px -20px 0;
  padding: 14px 20px;
  background: ${baseWhite};
  border-bottom: 1px solid ${skyLight};
  border-radius: 16px 16px 0 0;
`;

const FilePicker = styled.div`
  min-width: 220px;
`;

const PathBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-size: 11.5px;
`;

const Path = styled(Mono)`
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const KeptChip = styled.span`
  padding: 2px 8px;
  border-radius: 99px;
  background: ${secondaryLightest};
  color: ${secondaryDarkest};
  font: 600 10.5px/1.3 'Figtree', sans-serif;
  white-space: nowrap;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LoadingWrap = styled.div`
  position: relative;
  min-height: 160px;
`;

const Message = styled.span`
  font: 500 13px/1.5 'Figtree', sans-serif;
  color: ${inkLight};
`;
